import type { DayOfWeek, DaySchedule, Restaurant } from '@noor/eats-api';
import type { Venue, VenueHours } from '../model/schema';
import { formatHoursRange } from './working-hours';

// Mappers between the merchant API's restaurant/schedule payloads and the
// venue ("About") screen's view model.

/**
 * Weekday order for the schedule, Monday-first (the UI's reading order — the
 * API returns the days already sorted, but nothing guarantees it).
 */
const WEEKDAY_ORDER: readonly DayOfWeek[] = [
  'MONDAY',
  'TUESDAY',
  'WEDNESDAY',
  'THURSDAY',
  'FRIDAY',
  'SATURDAY',
  'SUNDAY',
] as const;

/** API weekday → the `venue.weekdays.<key>` i18n key the card renders. */
const LABEL_BY_DAY: Record<DayOfWeek, string> = {
  MONDAY: 'mon',
  TUESDAY: 'tue',
  WEDNESDAY: 'wed',
  THURSDAY: 'thu',
  FRIDAY: 'fri',
  SATURDAY: 'sat',
  SUNDAY: 'sun',
  DAY_OF_WEEK_UNSPECIFIED: '',
};

const DAY_BY_LABEL: Record<string, DayOfWeek> = {
  mon: 'MONDAY',
  tue: 'TUESDAY',
  wed: 'WEDNESDAY',
  thu: 'THURSDAY',
  fri: 'FRIDAY',
  sat: 'SATURDAY',
  sun: 'SUNDAY',
};

/**
 * Flatten the API's weekly schedule into the card's `{ label, hours }` rows,
 * in Monday-first order. A closed day carries an empty `hours` string, which is
 * exactly how the editor represents "closed".
 */
export function toVenueHours(schedule: DaySchedule[]): VenueHours[] {
  const byDay = new Map(schedule.map((day) => [day.day_of_week, day]));

  return WEEKDAY_ORDER.map((weekday) => {
    const day = byDay.get(weekday);
    const isOpen = day !== undefined && !day.is_closed && day.open_time && day.close_time;
    return {
      label: LABEL_BY_DAY[weekday],
      ...(day?.order_from ? { orderFrom: day.order_from } : {}),
      ...(day?.order_to ? { orderTo: day.order_to } : {}),
      hours: isOpen ? formatHoursRange(day.open_time, day.close_time) : '',
    };
  });
}

/**
 * Rebuild the API's schedule from the card's rows. Rows whose label isn't a
 * known weekday are dropped rather than sent as `DAY_OF_WEEK_UNSPECIFIED`,
 * which the backend would reject.
 */
export function toDaySchedule(workingHours: VenueHours[]): DaySchedule[] {
  const schedule: DaySchedule[] = [];

  for (const row of workingHours) {
    const weekday = DAY_BY_LABEL[row.label];
    if (!weekday) continue;

    const [open = '', close = ''] = row.hours.split(/\s*[-–—]\s*/).map((s) => s.trim());
    const isClosed = open === '' || close === '';
    schedule.push({
      day_of_week: weekday,
      open_time: isClosed ? '' : open,
      close_time: isClosed ? '' : close,
      is_closed: isClosed,
      ...(row.orderFrom !== undefined
        ? { order_from: isClosed ? '' : row.orderFrom }
        : {}),
      ...(row.orderTo !== undefined ? { order_to: isClosed ? '' : row.orderTo } : {}),
    });
  }

  return schedule;
}

/**
 * Map a restaurant plus its schedule to the venue profile.
 *
 * NO API SOURCE for the cover image: `v1Restaurant` has `icon` (the logo) and
 * `background_image`, so the cover falls back to the background image and is
 * left empty when the merchant hasn't uploaded one — the card renders its own
 * placeholder rather than showing a fabricated photo.
 */
export function toVenue(restaurant: Restaurant, schedule: DaySchedule[]): Venue {
  return {
    name: restaurant.name,
    description: restaurant.description,
    coverUrl: restaurant.background_image,
    logoUrl: restaurant.icon,
    ...(restaurant.address?.line ? { address: restaurant.address.line } : {}),
    location: {
      lat: restaurant.address?.latitude ?? 0,
      long: restaurant.address?.longitude ?? 0,
    },
    workingHours: toVenueHours(schedule),
  };
}
